import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, FormGroup } from '@angular/forms';
import { Response } from 'src/services/classes/models/response.interface';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { Logger } from 'src/services/core/logger/logger.service';
import { OrdersService } from 'src/services/data/orders/orders.service';
import { environment } from 'src/environments/environment.prod';
import { Router } from '@angular/router';
import { CredentialsService } from './authentication/credentials/credentials.service';
import { AccountService } from 'src/services/data/account/account.service';
import { Address } from 'src/services/classes/models/restaurant.interface';

const log = new Logger('App');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  isDrawerOpen = false;
  title = 'sm-kitchen-bar';
  rejectOrderForm!: FormGroup;
  selectedOption!: string;
  orderDetails!: any;
  showInput = false;
  restaurantDetails: any = null;
  credentials: any;
  isOnline = false;
  rejectionReasons = [
    {
      label: 'Insufficient inventory for one or more items.',
      value: 'Insufficient inventory for one or more items.',
      checked: false,
    },
    {
      label: 'Overloaded with orders at the moment.',
      value: 'Overloaded with orders at the moment.',
      checked: false,
    },
    { label: 'Other', value: 'Other', checked: false },
  ];

  isMenuDrawerOpen = false;

  constructor(
    private fb: UntypedFormBuilder,
    private ordersService: OrdersService,
    private notifyService: NotificationsService,
    private credentialsService: CredentialsService,
    private router: Router,
    private accountService: AccountService
  ) {}

  ngOnInit(): void {
    this.credentials = this.credentialsService.credentials;
    this.isOnline = this.credentialsService.isOnline();

    // this.getRestaurantInfo();
    addEventListener('open-drawer', (detail: any) => {
      setTimeout(() => {
        detail && detail['detail']
          ? (this.orderDetails = detail['detail'])
          : null;
        this.isDrawerOpen = true;
      }, 0);
    });

    addEventListener('open-menu-drawer', () => {
      setTimeout(() => {
        this.isMenuDrawerOpen = true;
      }, 0);
    });

    addEventListener('reset-online-sidebar-status', () => {
      setTimeout(() => {
        this.isOnline = this.credentialsService.isOnline();
      }, 0);
    });

    this.initForm();

    if (environment.production) {
      Logger.enableProductionMode();
    }
  }

  initForm() {
    this.rejectOrderForm = this.fb.group({
      reason: ['', Validators.required],
    });
  }

  closeDrawer(event: any) {
    if (event === 'close-drawer') {
      this.isDrawerOpen = false;
    }
  }

  handleReasonCollection(option: any) {
    this.selectedOption = option.value;

    if (option.value === 'Other') {
      this.showInput = true;
    } else {
      this.showInput = false;
      this.rejectOrderForm.patchValue({
        reason: option.value,
      });
    }
  }

  updateFormValue(reason: string) {
    this.rejectOrderForm.patchValue({
      reason,
    });
  }

  submitForm() {
    const payload = this.rejectOrderForm.value;
    payload['orderId'] = this.orderDetails?.orderId;

    log.debug('payload: ', payload);
    this.rejectOrder(payload);
  }

  rejectOrder(payload: any) {
    const rejection$ = this.ordersService.rejectOrder(payload);
    rejection$.subscribe({
      next: (res: Response) => {
        if (res.status.toLowerCase() === 'success') {
          res?.message
            ? this.notifyService.publishMessages(res.message, 'success', 1)
            : this.notifyService.publishMessages(
                'An error occurred',
                'success',
                1
              );
          this.isDrawerOpen = false;
          this.router.navigateByUrl('/live-orders');
          // this.triggerReloadOrderDetails();
        } else {
          res?.message
            ? this.notifyService.publishMessages(res.message, 'danger', 1)
            : this.notifyService.publishMessages(
                'An error occurred',
                'danger',
                1
              );
        }
      },
      error: (error: any) => log.debug('error: ', error),
    });
  }

  triggerReloadOrderDetails() {
    dispatchEvent(
      new CustomEvent('reload-order', {
        bubbles: false,
        cancelable: false,
      })
    );
  }

  closeMenuDrawer(event: any) {
    this.isMenuDrawerOpen = false;
  }

  createAddress(address: Address | undefined) {
    if (address !== null) {
      const addressLine = address?.line || '';
      const addressCity = address?.city || '';
      const addressState = address?.state || '';
      const addressCountry = address?.country || '';
      const fullAddress = `${addressLine} ${addressCity}, ${addressState}, ${addressCountry}`;

      return fullAddress || '';
    }
    return '';
  }

  getRestaurantInfo() {
    const restaurantInfo = this.credentialsService.credentials?.restaurant;
    const details = {
      name: restaurantInfo?.name,
      address: this.createAddress(restaurantInfo?.address),
      logoUrl: restaurantInfo?.logoUrl,
    };
    this.restaurantDetails = details;
  }

  changeOnlineStatus(status: boolean) {
    const onlineStatus$ = this.accountService.updateOnlineStatus(status);
    onlineStatus$.subscribe({
      next: (res: Response) => {
        if (res.status.toLowerCase() === 'success') {
          if (res.data) {
            const credentials: any = {
              ...this.credentialsService.credentials,
              onlineStatus: res.data.status,
            };
            this.credentialsService.setCredentials(credentials);
            this.isOnline = res.data.status;
          }
          res?.message
            ? this.notifyService.publishMessages(res.message, 'success', 1)
            : this.notifyService.publishMessages(
                'Online status updated',
                'success',
                1
              );

          dispatchEvent(
            new CustomEvent('reset-online-status', {
              bubbles: false,
              cancelable: false,
            })
          );
        } else {
          res?.message
            ? this.notifyService.publishMessages(res.message, 'danger', 1)
            : this.notifyService.publishMessages(
                'An error occurred',
                'danger',
                1
              );
        }
      },
      error: (error) => log.debug('error: ', error),
    });
  }

  logout() {
    this.isMenuDrawerOpen = false;
    this.router.navigateByUrl('/auth');
    this.credentialsService.setCredentials();
  }
}
